// Today's palette and keyboard. Online, it asks the server (`GET /v1/search`),
// debounced, for issues, plans, plan comments and issue record entries; the result a key or a plan's `#NNN` names
// leads, under "Go to". Until the server's first answer, offline, or when the
// server fails, it searches the issues, plans, comments and records the store holds.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Issue, PlanDetail, PlanRow, PlanStatus, Project, RecordEntry, SearchAnswer, SearchKind, StateSlug } from '../api/types'
import { DocIcon, PlanStatusIcon, SearchIcon, StateIcon } from '../components/Icons'
import { sameKey } from '../lib/keys'
import { isHtmlBody } from '../plans/format'
import { inert, readable } from '../plans/parse'
import { planPath } from '../projects/routing'
import { useBoardState, useStore } from '../store/context'
import { cx } from '../transcript/bits'
import { planStatusName, stateName } from '../vocab'
import { canInteract, useTabScope } from '../workspace/context'
import { highlightTerms, leadToFirstMatch, plainSnippet, queryTerms } from './snippet'
import { words } from './words'
import { docsPath } from '../docs/links'

/** How long the palette waits after a keystroke before it asks the server. */
export const SEARCH_DEBOUNCE_MS = 60
/** The most results it shows, as today's palette. */
const LIMIT = 20

/** A result, the server's or the store's, as the palette shows it. */
interface Item {
  kind: SearchKind
  id: string
  /** Where it opens. */
  path: string
  /** An issue's key, or a plan's `#NNN`. */
  key: string | null
  title: string
  project: string
  snippet: string
  state: StateSlug | null
  status: PlanStatus | null
  /** A record result: the entry's place in its issue's record. */
  entry?: number | null
}

/** Each term starts a word, ignoring case and accents, as today's search. */
function matches(text: string, terms: readonly string[]): boolean {
  const words = queryTerms(text)
  return terms.every((term) => words.some((word) => word.startsWith(term)))
}

function issueItem(issue: Issue, snippet: string): Item {
  return { kind: 'issue', id: issue.id, path: `/${issue.key}`, key: issue.key, title: issue.title, project: issue.project, snippet, state: issue.state, status: null }
}

/** A cached HTML plan's visible words, with entities decoded and unread text dropped. */
function planText(body: string): string {
  return plainSnippet(isHtmlBody(body) ? readable(inert(body)) : body)
}

function pathOfPlan(projects: ReadonlyMap<string, Project>, project: string, id: string): string {
  const owner = projects.get(project)
  return owner ? planPath(owner.key, id) : `/plan/${encodeURIComponent(id)}`
}

function findCached(issues: ReadonlyMap<string, Issue>, plans: ReadonlyMap<string, PlanRow>, details: ReadonlyMap<string, PlanDetail>, entries: ReadonlyMap<string, RecordEntry>, projects: ReadonlyMap<string, Project>, query: string, project: string | null, all: boolean): Item[] {
  const terms = queryTerms(query)
  if (terms.length === 0) return []
  const results: { item: Item; rank: number }[] = []
  for (const issue of issues.values()) {
    if (!all && issue.project !== project) continue
    const texts = [issue.description, issue.plan.text, issue.outcome].map(plainSnippet)
    const title = `${issue.key} ${issue.title}`
    const exact = sameKey(query, issue.key)
    if (!exact && !matches([title, ...texts].join(' '), terms)) continue
    // Prefer the field that explains all the terms, then the first matching field.
    const snippet = texts.find((text) => matches(text, terms)) ?? texts.find((text) => terms.some((term) => matches(text, [term]))) ?? ''
    results.push({ item: issueItem(issue, snippet), rank: exact ? 0 : matches(title, terms) ? 1 : 2 })
  }
  // Plan rows are cached even before their body has been opened. A detail
  // can also hold an archived plan, absent from the snapshot's rows.
  const cachedPlans = new Map<string, PlanRow>([...details, ...plans])
  for (const plan of cachedPlans.values()) {
    if (!all && plan.project !== project) continue
    const detail = details.get(plan.id)
    const path = pathOfPlan(projects, plan.project, plan.id)
    const title = `${plan.legacy_ref ?? ''} ${plan.title}`
    const text = [plan.summary, detail ? planText(detail.body) : ''].filter(Boolean).join(' ')
    const exact = query === plan.legacy_ref
    if (exact || matches(`${title} ${text}`, terms)) {
      results.push({ item: { kind: 'plan', id: plan.id, path, key: plan.legacy_ref, title: plan.title, project: plan.project, snippet: text, state: null, status: plan.status }, rank: exact ? 0 : matches(title, terms) ? 1 : 2 })
    }
    for (const comment of detail?.comments ?? []) {
      if (!matches(comment.body, terms)) continue
      results.push({ item: { kind: 'plan_comment', id: comment.id, path: `${path}#comment-${encodeURIComponent(comment.id)}`, key: plan.legacy_ref, title: plan.title, project: plan.project, snippet: plainSnippet(comment.body), state: null, status: null }, rank: 2 })
    }
  }
  for (const entry of entries.values()) {
    const issue = issues.get(entry.issue)
    if (!issue || (!all && issue.project !== project) || !matches(entry.body, terms)) continue
    results.push({ item: { kind: 'record', id: entry.id, path: `/${issue.key}#entry-${entry.number}`, key: issue.key, title: issue.title, project: issue.project, snippet: plainSnippet(entry.body), state: null, status: null, entry: entry.number }, rank: 2 })
  }
  return results.sort((a, b) => a.rank - b.rank).slice(0, LIMIT).map(({ item }) => item)
}

/** The server's results, with what the store knows of each: an issue's key and state, a plan's status. */
function answerItems(answer: SearchAnswer, issues: ReadonlyMap<string, Issue>, plans: ReadonlyMap<string, PlanRow>, projects: ReadonlyMap<string, Project>): Item[] {
  return answer.results.map((hit) => {
    const snippet = plainSnippet(hit.snippet)
    if (hit.kind === 'repo') return { kind: 'repo', id: hit.id, path: docsPath(projects.get(hit.project)?.key ?? hit.project, hit.path ?? ''), key: null, title: hit.title, project: hit.project, snippet, state: null, status: null }
    if (hit.kind === 'issue') {
      const issue = issues.get(hit.id)
      const key = issue?.key ?? hit.key ?? hit.id
      return { kind: 'issue', id: hit.id, path: `/${key}`, key, title: issue?.title ?? hit.title, project: hit.project, snippet, state: issue?.state ?? null, status: null }
    }
    if (hit.kind === 'record') {
      // An entry opens its place in the issue record.
      const key = hit.key ?? hit.issue ?? hit.id
      return { kind: 'record', id: hit.id, path: `/${key}${hit.entry != null ? `#entry-${hit.entry}` : ''}`, key, title: issues.get(hit.issue ?? '')?.title ?? hit.title, project: hit.project, snippet, state: null, status: null, entry: hit.entry ?? null }
    }
    const id = hit.kind === 'plan_comment' ? hit.plan ?? hit.id : hit.id
    const plan = plans.get(id)
    // An archived plan is not in the store, and opens all the same.
    const path = pathOfPlan(projects, hit.project, id) + (hit.kind === 'plan_comment' ? `#comment-${encodeURIComponent(hit.id)}` : '')
    return { kind: hit.kind, id: hit.id, path, key: hit.key ?? null, title: plan?.title ?? hit.title, project: hit.project, snippet, state: null, status: hit.kind === 'plan' ? plan?.status ?? null : null }
  })
}

/** Today's plain snippet, with the first matching word kept in view. */
function Snippet({ text, query }: { text: string; query: string }) {
  const parts = leadToFirstMatch(highlightTerms(text, query))
  return <span className="palette-snippet">{parts.map((part, index) => part.match ? <mark key={index}>{part.text}</mark> : part.text)}</span>
}

function ItemIcon({ item }: { item: Item }) {
  if (item.state) return <StateIcon state={item.state} />
  if (item.status) return <PlanStatusIcon status={item.status} />
  return item.kind === 'plan' ? <DocIcon size={14} /> : <SearchIcon size={14} />
}

export function SearchPalette({ onClose }: { onClose(): void }) {
  const scope = useTabScope()
  const navigate = useNavigate()
  const store = useStore()
  const { issues, plans, planDetails, entries, projects, selectedProjectId, offline } = useBoardState()
  const [query, setQuery] = useState('')
  const [allProjects, setAllProjects] = useState(false)
  const [active, setActive] = useState(0)
  /** The server's latest answer, and what it answered: shown until the next one arrives, as today's palette did. */
  const [answer, setAnswer] = useState<{ ask: string; found: SearchAnswer } | null>(null)
  /** The ask the server failed: its local results stay. */
  const [failed, setFailed] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const toggle = useRef<HTMLInputElement>(null)
  const listId = useId()
  const q = query.trim()
  const ask = JSON.stringify([q, selectedProjectId, allProjects])

  useEffect(() => {
    if (!q || offline) return
    let live = true
    const timer = setTimeout(() => {
      store.search(q, { project: selectedProjectId, all: allProjects, limit: LIMIT }).then(
        (found) => {
          if (!live) return
          setAnswer({ ask, found })
          setFailed(null)
          setActive(0)
        },
        () => { if (live) setFailed(ask) },
      )
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [store, q, selectedProjectId, allProjects, offline, ask])

  const server = q && !offline && failed !== ask ? answer?.found ?? null : null
  const items = useMemo(
    () => server ? answerItems(server, issues, plans, projects) : findCached(issues, plans, planDetails, entries, projects, q, selectedProjectId, allProjects),
    [server, issues, plans, planDetails, entries, projects, q, selectedProjectId, allProjects],
  )
  // The jump leads under "Go to"; then issues, plans, comments and records.
  const groups = useMemo(() => {
    const jump = server?.jump
    const lead = jump && items[0]?.id === jump.id ? items.slice(0, 1) : []
    const rest = items.slice(lead.length)
    return [
      { title: words.goTo, items: lead },
      { title: words.issues, items: rest.filter((item) => item.kind === 'issue') },
      { title: words.plans, items: rest.filter((item) => item.kind === 'plan') },
      { title: words.comments, items: rest.filter((item) => item.kind === 'plan_comment') },
      { title: 'Repository docs', items: rest.filter(item => item.kind === 'repo') },
      { title: words.record, items: rest.filter((item) => item.kind === 'record') },
    ].filter((group) => group.items.length > 0)
  }, [server, items])
  const shown = useMemo(() => groups.flatMap((group) => group.items), [groups])
  // A live store update may shrink the list while an arrow selection is held.
  const at = Math.min(active, Math.max(0, shown.length - 1))
  const optionId = (index: number) => `${listId}-option-${index}`

  useEffect(() => {
    const previous = document.activeElement
    if (canInteract(scope)) input.current?.focus()
    return () => { if (canInteract(scope) && previous instanceof HTMLElement && previous.isConnected) previous.focus() }
  }, [scope])
  useEffect(() => {
    document.getElementById(`${listId}-option-${at}`)?.scrollIntoView?.({ block: 'nearest' })
  }, [at, listId, shown])

  const go = (item: Item, newTab = false, foreground = false) => {
    if (!canInteract(scope)) return
    const path = item.path
    if (newTab && scope) window.dispatchEvent(new CustomEvent('poulpe-workspace-command', { detail: { type: 'open', path, disposition: foreground ? 'foreground' : 'background', source: scope.id } }))
    else if (newTab) window.open(path, '_blank', 'noopener')
    else navigate(path)
    onClose()
  }
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!canInteract(scope) || event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (shown.length) setActive((at + 1) % shown.length)
        break
      case 'ArrowUp':
        event.preventDefault()
        if (shown.length) setActive((at - 1 + shown.length) % shown.length)
        break
      case 'Enter': {
        event.preventDefault()
        const item = shown[at]
        if (item) go(item, event.metaKey || event.ctrlKey, event.shiftKey)
        break
      }
    }
  }

  let next = 0
  return (
    <div className="palette-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && canInteract(scope)) onClose() }}>
      <div className="palette" role="dialog" aria-modal="true" aria-label={words.search}
        onKeyDown={(event) => {
          if (event.defaultPrevented || !canInteract(scope) || event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return
          if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            onClose()
          } else if (event.key === 'Tab') {
            event.preventDefault()
            if (event.target === input.current) toggle.current?.focus()
            else input.current?.focus()
          }
        }}>
        <div className="palette-input">
          <SearchIcon size={16} />
          <input ref={input} role="combobox" aria-expanded={shown.length > 0} aria-controls={listId}
            aria-activedescendant={shown.length ? optionId(at) : undefined} aria-autocomplete="list" aria-label={words.search}
            placeholder={words.placeholder} value={query} onChange={(event) => { setQuery(event.target.value); setActive(0) }}
            onKeyDown={onKeyDown} spellCheck={false} autoComplete="off" />
        </div>
        <label className="palette-scope">
          <input ref={toggle} type="checkbox" checked={allProjects} onChange={(event) => { setAllProjects(event.target.checked); setActive(0) }} /> {words.allProjects}
        </label>
        <div className="palette-results" id={listId} role="listbox" aria-label={words.results}>
          {groups.map((group) => (
            <div key={group.title} role="group" aria-label={group.title} className="palette-group">
              <div className="palette-group-title" aria-hidden="true">{group.title}</div>
              {group.items.map((item) => {
                const index = next++
                return (
                  <a key={`${item.kind}:${item.id}`} href={item.path} id={optionId(index)} role="option" tabIndex={-1} aria-selected={index === at}
                    className={cx('palette-item', index === at && 'is-active')}
                    onMouseMove={() => setActive(index)}
                    onClick={(event) => {
                      if (!scope && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return
                      event.preventDefault()
                      go(item, event.metaKey || event.ctrlKey, event.shiftKey)
                    }}>
                    <ItemIcon item={item} />
                    <span className="palette-text">
                      <span className="palette-title">
                        {allProjects && <span className="palette-key">{projects.get(item.project)?.key}</span>}
                        {item.key && <><span className="palette-key">{item.key}</span>{' '}</>}
                        <span className="palette-name">{item.title}</span>
                        {item.state && <>{' '}<span className="palette-state">{stateName(item.state)}</span></>}
                        {item.status && <>{' '}<span className="palette-state">{planStatusName(item.status)}</span></>}
                        {item.entry != null && <>{' '}<span className="palette-state">#{item.entry}</span></>}
                      </span>
                      {item.snippet && <Snippet text={item.snippet} query={q} />}
                    </span>
                  </a>
                )
              })}
            </div>
          ))}
          {q && shown.length === 0 && <p className="palette-empty">No results for “{q}”.</p>}
          {!q && <p className="palette-empty">{words.empty}</p>}
        </div>
        <footer className="palette-footer" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
        </footer>
      </div>
    </div>
  )
}
